// purpose: レイヤースタック（エンティティ + FX）へのブルーム。pixi-filters の AdvancedBloomFilter を構築失敗許容で包む唯一の窓口。

import type { Container } from 'pixi.js';
import { AdvancedBloomFilter } from 'pixi-filters';

/**
 * Bloom is the one heavy pass in this renderer, so every number here is a
 * budget decision rather than a taste one.
 *
 * The threshold sits high on purpose: everything inside the bloomed container is
 * native pixel art, and a low threshold hazes the entity sprites instead of
 * lighting the FX. The additive layers (beams, streaks, exhaust, muzzle, sparks,
 * bursts, rings) are the only things that reliably clear 0.45, which is exactly
 * the intent — the glow belongs to the effects, not to the ships.
 *
 * `resolution = 1` pins the pass to the backing store. The app runs at
 * resolution 1 with roundPixels, and letting the filter pick its own resolution
 * would put non-integer sampling back into a pixel-art frame.
 *
 * Construction is guarded: headless and CI WebGL varies, and a renderer that
 * throws on a cosmetic filter is worse than one that draws without it.
 */
export const BLOOM_THRESHOLD = 0.45;
export const BLOOM_SCALE = 0.8;
/** flashReduction halves the glow rather than removing it — the setting is about intensity, not about layout. */
export const BLOOM_SCALE_REDUCED = BLOOM_SCALE / 2;
export const BLOOM_BRIGHTNESS = 1;
export const BLOOM_BLUR = 4;
export const BLOOM_QUALITY = 3;

export class BloomStage {
  private filter: AdvancedBloomFilter | null = null;

  /**
   * Attaches the glow to a caller-owned container. The container keeps
   * ownership of itself; only the filter list is written here, and a failed
   * construction leaves that list untouched.
   */
  attach(target: Container, reduced: boolean): void {
    if (this.filter) return;
    try {
      const filter = new AdvancedBloomFilter({
        threshold: BLOOM_THRESHOLD,
        bloomScale: reduced ? BLOOM_SCALE_REDUCED : BLOOM_SCALE,
        brightness: BLOOM_BRIGHTNESS,
        blur: BLOOM_BLUR,
        quality: BLOOM_QUALITY,
      });
      filter.resolution = 1;
      target.filters = [filter];
      this.filter = filter;
    } catch {
      this.filter = null;
    }
  }

  setReduced(reduced: boolean): void {
    const filter = this.filter;
    if (!filter) return;
    filter.bloomScale = reduced ? BLOOM_SCALE_REDUCED : BLOOM_SCALE;
  }

  /** The container is destroyed by its owner; the filter is not a child, so it is dropped here. */
  dispose(): void {
    const filter = this.filter;
    this.filter = null;
    if (filter) filter.destroy();
  }
}
